import DateFnsUtils from '@date-io/date-fns';
import {
  Button,
  FormControl,
  Grid,
  LinearProgress,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { dateISOFomatter } from '../../utils/formatter';
import { useFetchApi } from '../../utils/services/api/useFetchApi';
import { TransferItemReportModal } from '../../components/modals/TransferItemReportModal';
import BranchPicker from '../../components/Input/BranchPicker';
import { read, utils, writeFileXLSX } from 'xlsx';
import { format } from 'date-fns';
import { title } from 'process';

export const TransferReportPage = observer(() => {
  const classes = useStyles();

  const [fromDate, setFromDate] = useState<Date | null>(() => {
    const dateFns = new DateFnsUtils();
    return dateFns.addDays(new Date(), -30);
  });
  const [toDate, setToDate] = useState<Date | null>(new Date());
  const [status, setStatus] = useState<ITransferSupply['status']>();
  const [openingItem, setOpeningItem] = useState<ITransferSupply | null>(null);
  const [fromBranch, setFromBranch] = useState<string | null>(null);
  const [toBranch, setToBranch] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const { data: transfers, isValidating } = useFetchApi<ITransferSupply[]>(
    '/reports/stock/transfer/',
    {
      params: {
        fromDate,
        toDate,
        status,
        fromBranch,
        toBranch,
      },
    }
  );

  const handleOnPressExport = async () => {
    if (exporting) return;

    setExporting(true);
    const suppliesDict = {};
    try {
      const exportingData = transfers?.map((item) => {
        if (!item) return null;

        const supplies =
          item.supplies?.map((supply) => {
            if (supply.supply) {
              const id = supply._id;
              suppliesDict[id] = supply.supply;
            }
            return `${supply.supply.name}: ${supply.amount} ${supply.supply.unit}`;
          }) || [];

        console.log(JSON.stringify(suppliesDict, null, 2));
        const receivedSupplies =
          item.receivedSupplies?.map((supply) => {
            if (
              supply.supply != null &&
              typeof supply.supply === 'object' &&
              !Array.isArray(supply.supply)
            ) {
              return `${supply.supply.name}: ${supply.amount} ${supply.supply.unit}`;
            } else if (
              supply.supply != null &&
              typeof supply.supply === 'object' &&
              Array.isArray(supply.supply) &&
              supply.supply.length === 0
            ) {
              const supplyData = suppliesDict[supply._id];
              return `${supplyData?.name}: ${supply.amount} ${supplyData?.unit}`;
            }

            return '';
          }) || [];

        console.log('supplies', supplies.join(',\n'));
        console.log('receivedSupplies', receivedSupplies.join(',\n'));

        return {
          title: item.title || '-',
          date: dateISOFomatter(item.createdAt || '', 'ccc, dd-MM-yyyy HH:mm'),
          fromBranch: item.branch.name,
          toBranch: item.toBranch.name,
          status: item.status,
          supplies: supplies.join(',\n'),
          receivedSupplies: receivedSupplies.join(',\n'),
          sender: item.actor.firstname || '-',
          receiver: item.receiver?.firstname || '-',
          note: item.note || '-',
        };
      });

      const worksheet = utils.json_to_sheet(exportingData);
      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, worksheet, 'Transfer Report');
      const dateStr = format(new Date(), 'yyyy-MM-dd');
      const fileName = `Transfer_Report_${dateStr}.xlsx`;
      writeFileXLSX(workbook, fileName);
      console.log('Exported transfer report successfully:', fileName);
      alert(`Exported transfer report successfully: ${fileName}`);
    } catch (error) {
      console.error('Error exporting transfer report:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleOpenItem = (item: ITransferSupply) => () => {
    setOpeningItem(item);
  };

  return (
    <div className={classes.root}>
      <div>
        <Typography variant="h4" align="left">
          Transfer Report
        </Typography>
        <Button
          variant="contained"
          color="primary"
          disabled={exporting}
          onClick={handleOnPressExport}
        >
          {exporting ? 'Exporting...' : 'Export'}
        </Button>
      </div>
      <Paper className={classes.paper}>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <Grid container justify="flex-start" className={classes.inputGrid}>
            <Grid item className={classes.marginRight}>
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="MM/dd/yyyy"
                margin="normal"
                id="date-picker-inline"
                label="from date"
                value={fromDate}
                onChange={setFromDate}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
            </Grid>
            <Grid item>
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="MM/dd/yyyy"
                margin="normal"
                id="date-picker-inline"
                label="to date"
                value={toDate}
                onChange={setToDate}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
            </Grid>
            <FormControl className={classes.formControl} required>
              <Select
                name="status"
                value={status}
                label="status"
                placeholder="status"
                displayEmpty={true}
                onChange={(e) => setStatus(e.target.value as ITransferSupply['status'])}
              >
                <MenuItem key={'ALL'} value={''}>
                  {'ALL'}
                </MenuItem>
                <MenuItem key={'TRANSFERRED'} value={'TRANSFERRED'}>
                  {'TRANSFERRED'}
                </MenuItem>
                <MenuItem key={'RECEIVED'} value={'RECEIVED'}>
                  {'RECEIVED'}
                </MenuItem>
                <MenuItem key={'LOST'} value={'LOST'}>
                  {'LOST'}
                </MenuItem>
              </Select>
            </FormControl>
            <BranchPicker
              label="จากสาขา"
              name="fromBranch"
              noneOptionTitle="ทั้งหมด"
              classname={classes.branchPicker}
              onSelect={setFromBranch}
            />
            <BranchPicker
              label="ไปยังสาขา"
              name="toBranch"
              noneOptionTitle="ทั้งหมด"
              classname={classes.branchPicker}
              onSelect={setToBranch}
            />
          </Grid>
        </MuiPickersUtilsProvider>
      </Paper>
      <Paper className={classes.paper}>
        <LinearProgress hidden={!isValidating} />
        <Table className={classes.table} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>title</TableCell>
              <TableCell>วันที่</TableCell>
              <TableCell>จากสาขา</TableCell>
              <TableCell>ไปยังสาขา</TableCell>
              <TableCell>สถานะ</TableCell>
              <TableCell>ผู้ส่ง</TableCell>
              <TableCell>ผู้รับ</TableCell>
              <TableCell>โน๊ต</TableCell>
              <TableCell>actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transfers?.map((row) => (
              <TableRow key={row._id}>
                <TableCell>{row.title || '-'}</TableCell>
                <TableCell>
                  {dateISOFomatter(row.createdAt ?? '', 'ccc, dd-MM-yyyy HH:mm')}
                </TableCell>
                <TableCell>{row.branch.name}</TableCell>
                <TableCell>{row.toBranch.name}</TableCell>
                <TableCell>{row.status}</TableCell>
                <TableCell>{row.actor.firstname || '-'}</TableCell>
                <TableCell>{row.receiver?.firstname || '-'}</TableCell>
                <TableCell>{row.note || '-'}</TableCell>
                <TableCell>
                  <Button variant="contained" onClick={handleOpenItem(row)}>
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <TransferItemReportModal
        open={!!openingItem}
        handleClose={() => setOpeningItem(null)}
        item={openingItem}
      />
    </div>
  );
});

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  paper: {
    marginTop: theme.spacing(3),
    overflowX: 'auto',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 500,
  },
  textField: {
    minWidth: 200,
  },
  menu: {
    minWidth: 200,
  },
  margin: {
    margin: theme.spacing(1),
  },
  marginRight: {
    marginRight: theme.spacing(2),
  },
  inputGrid: {
    padding: theme.spacing(1),
  },
  formControl: {
    marginLeft: theme.spacing(2),
    minWidth: 120,
    alignSelf: 'flex-end',
    top: -7,
  },
  branchPicker: {
    marginLeft: theme.spacing(2),
    minWidth: 120,
    top: 8,
  },
}));
